import {
  EventSummary,
  EventWithCustomField,
  MinimalEventForParticipation,
} from "@ac-expo/api-types";
import { formatEventDate, getAddressLabel, getEventEndDate } from "@ac-expo/utils";
import { FileType } from "@prisma/client";
import { isAfter, isBefore, isWithinInterval } from "date-fns";
import { useMemo, useState } from "react";
import { cn } from "../../../../utils";
import { Loading } from "../../Loading";
import CardEvent, { CARD_EVENT_THUMBNAIL_SIZE } from "./CardEvent";

const baseButtonStyle = "w-1/3 rounded-2xl border";
const activeStyle = "border-primary-500 text-primary-500 border-b-2 bg-blue-50";
const inactiveStyle = "text-gray-600";

export function ParticipationPanel({
  participations,
  onEventCardClick,
  getFileUrl,
}: {
  participations: EventWithCustomField<EventSummary>[] | MinimalEventForParticipation[];
  onEventCardClick?: (eventId: string, eventSlug: string | null) => void;
  getFileUrl: (key?: string, type?: FileType, width?: number) => string;
}) {
  const [filter, setFilter] = useState("upcoming");

  const filteredEvents: (EventWithCustomField<EventSummary> | MinimalEventForParticipation)[] =
    useMemo(() => {
      const now = new Date();

      return participations?.filter((event) => {
        if (!!event.dates && event.dates.schedules.length > 0) {
          const firstSchedule = event.dates.schedules[0];
          const lastSchedule = event.dates.schedules[event.dates.schedules.length - 1];

          const startDate = firstSchedule.fromDate;
          const endDate = lastSchedule.toDate;

          switch (filter) {
            case "upcoming":
              return isAfter(startDate, now);
            case "ongoing":
              return isWithinInterval(now, { start: startDate, end: endDate });
            case "past":
              return isBefore(endDate, now);
            default:
              return false;
          }
        }
        return false;
      });
    }, [filter, participations]);

  if (!filteredEvents) return <Loading />;

  return (
    <div className="size-full overflow-y-auto flex flex-col gap-4 p-0">
      <div className="flex gap-6">
        <button
          className={cn(baseButtonStyle, {
            [activeStyle]: filter === "upcoming",
            [inactiveStyle]: filter !== "upcoming",
          })}
          onClick={() => setFilter("upcoming")}
        >
          À venir
        </button>
        <button
          className={cn(baseButtonStyle, {
            [activeStyle]: filter === "ongoing",
            [inactiveStyle]: filter !== "ongoing",
          })}
          onClick={() => setFilter("ongoing")}
        >
          En cours
        </button>
        <button
          className={cn(baseButtonStyle, {
            [activeStyle]: filter === "past",
            [inactiveStyle]: filter !== "past",
          })}
          onClick={() => setFilter("past")}
        >
          Passées
        </button>
      </div>
      {filteredEvents.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 pt-6">
          <p className="text-gray-600">
            Aucune participation{" "}
            {filter === "upcoming" ? "à venir" : filter === "ongoing" ? "en cours" : "passée"} pour
            le moment.
          </p>
        </div>
      )}
      <ParticipationPanelEvents
        events={filteredEvents}
        onEventCardClick={onEventCardClick}
        getFileUrl={getFileUrl}
      />
    </div>
  );
}

function ParticipationPanelEvents({
  events,
  onEventCardClick,
  getFileUrl,
}: {
  events: (EventWithCustomField<EventSummary> | MinimalEventForParticipation)[];
  onEventCardClick?: (eventId: string, eventSlug: string | null) => void;
  getFileUrl: (key?: string, type?: FileType, width?: number) => string;
}) {
  const getIsTicketingAvailable = (
    event: EventWithCustomField<EventSummary> | MinimalEventForParticipation,
  ) => {
    const now = new Date();
    const isPassed = isBefore(getEventEndDate(event.dates), now);
    if ("hasTicketsOrBadges" in event) {
      return !isPassed && event.isTicketingAvailable && event.hasTicketsOrBadges;
    }

    return (
      !isPassed &&
      event.isTicketingAvailable &&
      (event.ticketTypes.length > 0 || event.badgeTypes.length > 0)
    );
  };

  return (
    <div className="gap-6 grid xl:grid-cols-[repeat(auto-fill,minmax(350px,1fr))] grid-cols-[repeat(auto-fill,minmax(250px,1fr))]">
      {events.map((event, index) => {
        const formattedDate = formatEventDate(
          event.dates?.schedules ?? [],
          event.dates?.exceptionalClosingDates ?? [],
        );
        const imageUrl =
          event.image !== null
            ? getFileUrl(event.image.key, event.image.type, CARD_EVENT_THUMBNAIL_SIZE)
            : null;
        const address = event.eventPlace
          ? event.eventPlace.place.placeEntrances[0].address.name
          : getAddressLabel(event.address);

        return (
          <div key={index} className="flex flex-col items-center justify-center gap-2">
            <CardEvent
              sponsored={event.isSponsored}
              date={formattedDate}
              imageUrl={imageUrl}
              eventName={event.name}
              subtitle={event.address?.name}
              location={address ?? "ville"}
              tags={[
                event.isPaidEntry ? "paid" : "free",
                event.isIndoor ? "indoor" : "",
                event.isOutdoor ? "outdoor" : "",
              ]}
              isTicketingAvailable={getIsTicketingAvailable(event)}
              onClick={() => onEventCardClick?.(event.id, event.slug)}
            />
          </div>
        );
      })}
    </div>
  );
}

export default ParticipationPanel;
